import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Panel track slide duration. Keep in sync with CSS
 * (`--auth-swipe-ms` on [data-auth-panel-track]) and the inactive-panel hide delay.
 */
export const AUTH_SWIPE_MS = 480

/**
 * Soft deceleration curve — reads smoother than linear ease-out for full-panel slides.
 * Matches a gentle “sheet settle” rather than a hard stop.
 */
export const AUTH_SWIPE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)"

/**
 * One half of the auth swipe track. The panel that slid away keeps its space so the
 * card never resizes mid-swipe, then goes visibility-hidden once the slide ends
 * so its fields leave the tab order.
 */
export function AuthSwipePanel({
  active,
  children,
}: {
  active: boolean
  children: React.ReactNode
}) {
  return (
    <div
      data-auth-panel
      data-auth-panel-active={active ? "true" : "false"}
      aria-hidden={!active || undefined}
      className={cn(
        "h-full w-full shrink-0",
        active ? "visible" : "invisible"
      )}
      style={
        active
          ? { visibility: "visible", transition: "none" }
          : {
              /* Hide only after the track finishes so tab order updates post-slide. */
              transition: `visibility 0s linear ${AUTH_SWIPE_MS}ms`,
              visibility: "hidden",
            }
      }
    >
      {children}
    </div>
  )
}

export function AuthSwipeViewport({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      data-auth-panel-viewport
      className={cn("relative h-full min-w-0 overflow-hidden", className)}
    >
      {children}
    </div>
  )
}

export function AuthSwipeTrack({
  offset,
  className,
  children,
}: {
  /** 0 = first panel, 1 = second panel (translateX by −100% × offset). */
  offset: 0 | 1
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      data-auth-panel-track
      data-auth-panel-offset={offset}
      className={cn(
        "flex h-full w-full transform-gpu will-change-transform motion-reduce:transition-none motion-reduce:will-change-auto",
        className
      )}
      style={
        {
          ["--auth-swipe-ms" as string]: `${AUTH_SWIPE_MS}ms`,
          ["--auth-swipe-easing" as string]: AUTH_SWIPE_EASING,
          transform:
            offset === 1 ? "translate3d(-100%, 0, 0)" : "translate3d(0, 0, 0)",
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  )
}
